import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Send, Unlink, Copy, Check, MessageSquare } from "lucide-react";

export default function TelegramLinkCard({ client, onChange }) {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(null);
  const [botUsername, setBotUsername] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/telegram/link/${client.id}`);
      setCode(data.code);
      setBotUsername(data.bot_username);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm("Scollegare il gruppo Telegram di questo cliente?")) return;
    setLoading(true);
    try {
      await api.post(`/telegram/unlink/${client.id}`);
      toast.success("Gruppo scollegato");
      setCode(null);
      onChange && onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const copyCmd = async () => {
    try {
      await navigator.clipboard.writeText(`/link ${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  const linked = client.telegram_linked;

  return (
    <div
      data-testid="telegram-link-card"
      className="rounded-3xl border border-[var(--mp-border)] bg-white p-6"
    >
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-[#0088cc] text-white grid place-items-center shrink-0">
          <Send className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Telegram
          </div>
          <div className="font-display text-xl tracking-tight mt-0.5">
            {linked ? "Gruppo collegato" : "Collega gruppo Telegram"}
          </div>
          {linked ? (
            <>
              <p className="mt-2 text-sm text-[var(--mp-muted)]">
                {client.telegram_chat_title ? (
                  <>
                    Gruppo: <strong>{client.telegram_chat_title}</strong>
                  </>
                ) : (
                  "Il gruppo Telegram di questo cliente è collegato al portale."
                )}{" "}
                Tutti i messaggi del gruppo compaiono nella chat qui sotto, e le tue risposte dal
                portale vengono postate automaticamente nel gruppo.
              </p>
              <div className="mt-4">
                <Button
                  variant="outline"
                  data-testid="telegram-unlink"
                  onClick={unlink}
                  disabled={loading}
                  className="rounded-full border-[var(--mp-ink)]"
                >
                  <Unlink className="h-4 w-4 mr-1" /> Scollega gruppo
                </Button>
              </div>
            </>
          ) : code ? (
            <>
              <p className="mt-2 text-sm text-[var(--mp-muted)]">
                Per completare il collegamento:
              </p>
              <ol className="mt-3 space-y-1.5 text-sm list-decimal ml-5">
                <li>
                  Apri il gruppo Telegram del cliente e aggiungi il bot{" "}
                  {botUsername && (
                    <a
                      href={`https://t.me/${botUsername}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium underline"
                    >
                      @{botUsername}
                    </a>
                  )}{" "}
                  come membro.
                </li>
                <li>
                  Scrivi nel gruppo il comando:
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-[var(--mp-ink)] bg-[var(--mp-yellow)] px-4 py-2 font-mono text-sm">
                    /link {code}
                    <button
                      onClick={copyCmd}
                      data-testid="telegram-copy-code"
                      className="ml-1 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold hover:opacity-80"
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> Copiato
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" /> Copia
                        </>
                      )}
                    </button>
                  </div>
                </li>
                <li>Il bot confermerà in chat "✅ Gruppo collegato al portale".</li>
                <li>
                  Poi torna qui e{" "}
                  <button
                    onClick={() => onChange && onChange()}
                    className="underline font-medium"
                  >
                    ricarica la scheda
                  </button>
                  .
                </li>
              </ol>
              <p className="text-[11px] text-[var(--mp-muted)] mt-3">
                Il codice scade tra 2 ore. Se scrivi <code className="font-mono">/link {code}</code> e il bot non risponde, prova <code className="font-mono">/link@{botUsername} {code}</code> oppure rendi il bot amministratore del gruppo (le impostazioni privacy del bot a volte bloccano i comandi).
              </p>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-[var(--mp-muted)]">
                Genera un codice di 6 cifre da incollare nel gruppo Telegram del cliente. Una volta
                collegato, il gruppo sarà in sync con la chat del portale.
              </p>
              <div className="mt-4">
                <Button
                  data-testid="telegram-link-generate"
                  onClick={generate}
                  disabled={loading}
                  className="rounded-full bg-[#0088cc] hover:bg-[#0077b3] text-white"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  {loading ? "Generazione…" : "Genera codice di collegamento"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
